import { ref } from 'vue'
import { ApiError } from '@/api/http'
import type { CreateOrderRequest, CreateFundRequestRequest } from '@/types/trading'

export type PendingOperation = { userId: string; key: string; createdAt: string } & (
  { kind: 'order'; payload: CreateOrderRequest } | { kind: 'fund'; payload: CreateFundRequestRequest }
)
export const pendingRevision = ref(0)
const storageKey = (userId: string) => `btp.pending-operation.${userId}`

function readStoredPending(userId: string): PendingOperation | null {
  const raw = localStorage.getItem(storageKey(userId))
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as PendingOperation
    if (value.userId !== userId || typeof value.key !== 'string' || !value.key
      || !value.payload || typeof value.payload !== 'object' || !['order', 'fund'].includes(value.kind)) throw new Error()
    return value
  } catch { throw new Error('待確認操作無法讀取，請聯絡維護人員核對，暫勿建立新操作。') }
}

async function withPendingLock<T>(userId: string, action: () => T | Promise<T>): Promise<T> {
  if (!navigator.locks?.request) throw new Error('目前瀏覽器無法安全鎖定操作，請使用支援此功能的瀏覽器。')
  return navigator.locks.request(storageKey(userId), { mode: 'exclusive' }, action)
}

export function readPending(userId?: string): PendingOperation | null {
  if (!userId) return null
  try {
    return readStoredPending(userId)
  } catch { return null }
}

export async function savePending(operation: PendingOperation) {
  const { userId } = operation
  // Snapshot before waiting for another tab's lock; later form changes cannot alter the reservation.
  const serialized = JSON.stringify(operation)
  await withPendingLock(userId, () => {
    if (readStoredPending(userId)) {
      pendingRevision.value++
      throw new Error('尚有一筆操作等待確認，請先在頁面上方核對結果。')
    }
    // Persist before sending: storage failure must not leave an untraceable request.
    localStorage.setItem(storageKey(userId), serialized)
    pendingRevision.value++
  })
}

export async function clearPending(userId: string, key: string, isCurrent: () => boolean = () => true) {
  await withPendingLock(userId, () => {
    if (!isCurrent()) return
    if (readStoredPending(userId)?.key !== key) return
    localStorage.removeItem(storageKey(userId))
    pendingRevision.value++
  })
}

export async function readPendingForRecovery(userId: string, key: string): Promise<PendingOperation | null> {
  return withPendingLock(userId, () => {
    const operation = readStoredPending(userId)
    return operation?.key === key ? operation : null
  })
}

export function isDefinitiveRejection(cause: unknown): boolean {
  if (cause instanceof ApiError && cause.requestSent === false) return true
  return cause instanceof ApiError && !!cause.status && cause.status >= 400 && cause.status < 500
    && ![408, 429].includes(cause.status) && cause.code !== 'DATA_CONFLICT'
}
